import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../../../../redux/store";

/**
 * The terms-and-conditions entry in a tenant's `content.hints` list. The `url`
 * values are a typed union on `TenantConfig`, so this literal is checked.
 */
const TERMS_HINT_URL = "TermOfServicePage";

/**
 * The tenant's terms-and-conditions component, or `null` when the tenant has no
 * component-based hints (every legacy tenant, whose hint pages come from the
 * backend `hints` list instead).
 *
 * Exists so the booking flow can render the terms **in place** rather than
 * navigating to `/hints/...`. Navigating away unmounts the flow's route, and
 * `TemporaryStorageNewBookingPage`'s mount effect dispatches
 * `newBookingStepChanged({ step: locationSelection })` — so coming back, by the
 * dialog's close or by the browser's back button, restarts the wizard at step 1.
 * From the customer's side that is simply "reading the T&Cs threw me out of the
 * booking".
 */
export const useTenantTermsPage = (): React.FC<{ lang: string }> | null => {
  const { i18n } = useTranslation();
  const config = useSelector((state: RootState) => state.tenantConfig.config);
  const hints = config?.content?.[i18n.language]?.hints;
  return hints?.find((hint) => hint.url === TERMS_HINT_URL)?.component ?? null;
};

/** The search param that holds the in-place terms sheet open. */
export const TERMS_PARAM = "terms";

/** The search param that holds the in-place withdrawal information open. */
export const WITHDRAWAL_PARAM = "withdrawal";

/**
 * The open/close state of ONE in-place sheet, held in the search param
 * `param`. The building block behind `useInPlaceTerms` and
 * `useInPlaceWithdrawal`; a screen that shows several documents in place uses
 * one per document, each on its own param.
 *
 * The open state lives in a search param rather than component state, so the
 * browser's back button closes the sheet instead of leaving the screen. A
 * search param does not change the pathname, so `<Routes>` keeps matching and
 * nothing unmounts. Other params on the URL (the acceptance screen's
 * `order_id` and `t`, the subscription flow's plan and location) are carried
 * over untouched.
 */
export const useInPlaceSheet = (param: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const open = searchParams.get(param) === "1";

  const setParam = (next: boolean, replace: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set(param, "1");
    } else {
      params.delete(param);
    }
    setSearchParams(params, { replace });
  };

  // Opening pushes, so `back` pops straight back to the screen. Closing
  // replaces rather than calling `navigate(-1)`, so the sheet also closes
  // cleanly when it was opened from a pasted URL with no history entry behind
  // it.
  const openSheet = () => setParam(true, false);
  const closeSheet = () => setParam(false, true);

  return { open, openSheet, closeSheet };
};

/**
 * The tenant's terms page plus the open/close state of the in-place sheet that
 * shows it. One implementation for every screen that must show the terms
 * without leaving its route — the booking flow's customer-data step, the
 * postpay acceptance screen and the kolula KEY subscription summary.
 */
export const useInPlaceTerms = () => {
  const TermsPage = useTenantTermsPage();
  const { open, openSheet, closeSheet } = useInPlaceSheet(TERMS_PARAM);
  return {
    TermsPage,
    termsOpen: open,
    openTerms: openSheet,
    closeTerms: closeSheet,
  };
};

/**
 * The open/close state of the in-place withdrawal information
 * (`WithdrawalInformation`). Unlike the terms there is no per-tenant page to
 * look up: the document is the statutory model, filled with the tenant's own
 * identity from its config.
 */
export const useInPlaceWithdrawal = () => {
  const { open, openSheet, closeSheet } = useInPlaceSheet(WITHDRAWAL_PARAM);
  return {
    withdrawalOpen: open,
    openWithdrawal: openSheet,
    closeWithdrawal: closeSheet,
  };
};
